import { ConsentMethod } from '../enums/ConsentMethod.enum';

export interface ConsentMetadata {
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
  method: ConsentMethod;
  deviceInfo?: {
    platform: string;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
  };
}

/**
 * Get browser and device information for consent tracking
 */
export function getConsentMetadata(method: ConsentMethod): ConsentMetadata {
  const timestamp = new Date().toISOString();
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  const deviceInfo = userAgent
    ? {
        platform: getPlatform(userAgent),
        isMobile: isMobileDevice(userAgent),
        isTablet: isTabletDevice(userAgent),
        isDesktop: isDesktopDevice(userAgent),
      }
    : undefined;

  return {
    userAgent,
    timestamp,
    method,
    deviceInfo,
  };
}

/**
 * Determine the platform from user agent
 */
function getPlatform(userAgent: string): string {
  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Mac/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iOS|iPhone|iPad/i.test(userAgent)) return 'iOS';

  return 'Unknown';
}

/**
 * Check if device is mobile
 */
function isMobileDevice(userAgent: string): boolean {
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}

/**
 * Check if device is tablet
 */
function isTabletDevice(userAgent: string): boolean {
  return /iPad|Android(?=.*\bMobile\b).*Tablet|KFAPWI|Tablet/i.test(userAgent);
}

/**
 * Check if device is desktop
 */
function isDesktopDevice(userAgent: string): boolean {
  return !isMobileDevice(userAgent) && !isTabletDevice(userAgent);
}

/**
 * Format consent method for display
 */
export function formatConsentMethod(method: string): string {
  switch (method) {
    case 'DIGITAL_SIGNATURE':
      return 'Digital Signature';
    case 'WEB_FORM':
      return 'Web Form';
    case 'KIOSK':
      return 'Kiosk';
    case 'MANUAL_ENTRY':
      return 'Manual Entry';
    case 'VERBAL':
      return 'Verbal Consent';
    case 'WRITTEN':
      return 'Written Consent';
    case 'EMAIL_CONFIRMATION':
      return 'Email Confirmation';
    case 'SMS_CONFIRMATION':
      return 'SMS Confirmation';
    case 'DOUBLE_OPT_IN':
      return 'Double Opt-in';
    default:
      return method
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
  }
}
